import { NotificationHandler } from '@sit/notifications-client';
import { NotificationPayload, NotificationTopic } from '@sit/notifications-shared';
import isEqual from 'lodash/isEqual';
import { useCallback, useContext, useEffect, useState } from 'react';
import { NotificationHandlerContext } from '../NotificationHandlerContext';

const handleNotificationMatch = (
  notification: NotificationPayload,
  topic: NotificationTopic,
  params: Record<string | number, unknown> | null = null,
): NotificationPayload | null => {
  const topicMatch = notification.topic === topic;

  const { data } = notification;
  const paramsMatch = !params ? true : data && Object.keys(params).every((key) => isEqual(data[key], params[key]));

  return topicMatch && paramsMatch ? notification : null;
};

export const useOnLatestNotification = (topic: NotificationTopic, handler: NotificationHandler) => {
  const { service: notificationHandlerService } = useContext(NotificationHandlerContext);

  return useEffect(() => notificationHandlerService.addHandler(topic, handler), [topic, handler, notificationHandlerService]);
};

export function useLatestNotification(
  topic: NotificationTopic,
  params?: Record<string | number, unknown>,
  subscribeToNotification?: NotificationHandler,
): NotificationPayload | undefined {
  const { service: notificationHandlerService } = useContext(NotificationHandlerContext);
  const [notification, setNotification] = useState<NotificationPayload | undefined>(undefined);

  const handleNotification = useCallback(
    (incomingNotification: NotificationPayload): void => {
      if (handleNotificationMatch(incomingNotification, topic, params)) {
        subscribeToNotification?.(incomingNotification);
        setNotification(incomingNotification);
      }
    },
    [params, topic, subscribeToNotification],
  );

  useEffect(
    () => notificationHandlerService.addHandler(topic, handleNotification),
    [topic, handleNotification, notificationHandlerService],
  );

  return notification;
}
